import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '@/context/SnackbarContext';
import { GLOBAL_PROMPT_INSIGHTS_QUERY_KEY } from '@/hooks/useFetchGlobalPromptInsights';
import useFetchInsightsJobStatus from '@/components/globalPrompts/hooks/useFetchInsightsJobStatus';
import { INSIGHTS_PENDING_RUN_KEY } from '@/components/globalPrompts/hooks/useGenerateInsights';

// Must match ErrorMessages.NO_CALLS_IN_SELECTED_FILTERS on the server.
const NO_CALLS_MESSAGE = 'No calls found in the selected filters';

// Mount this once in the app shell, not on the Insights tab.
// Why: the user can navigate away while a re-run is going. Mounting it in the shell means
// the status keeps being polled and the "done" toast still appears, whatever page they are on.
const InsightsJobWatcher: React.FC = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const { status, error, jobId, isConnectionError } = useFetchInsightsJobStatus();

    // The id of the last job we already reacted to.
    // Why: showing the toast causes a re-render, which runs this effect again. Without
    // this we would reload the insights and show the toast over and over for one job.
    // A ref is used rather than a marker in the query cache, because React Query ignores
    // a cache value set to undefined.
    const lastHandledJobId = useRef<number | undefined>(undefined);
    // Makes sure the "lost connection" toast appears only once per outage.
    const connectionErrorShown = useRef(false);

    useEffect(() => {
        // Polling gave up because the server is unreachable. The last status we saw is
        // still "running", so nothing else will ever tell the user it stopped. Say so here.
        if (isConnectionError) {
            if (!connectionErrorShown.current && localStorage.getItem(INSIGHTS_PENDING_RUN_KEY) !== null) {
                connectionErrorShown.current = true;
                showSnackbar('Lost connection while analyzing. Please try again.', 'error');
            }
            return;
        }
        connectionErrorShown.current = false;

        if (jobId === undefined || (status !== 'succeeded' && status !== 'failed')) {
            return;
        }
        if (jobId === lastHandledJobId.current) {
            return; // Already handled this job. Stops the reload and toast repeating.
        }

        // Only react to a run this user actually started. useGenerateInsights records its id.
        // Why: on load, the status endpoint returns the client's most recent job, which may
        // have finished days ago. Without this check every page load would show a toast.
        // The id is kept in localStorage so this still works if the page was refreshed
        // while the run was going.
        const pendingJobId = localStorage.getItem(INSIGHTS_PENDING_RUN_KEY);
        if (pendingJobId === null || Number(pendingJobId) !== jobId) {
            return;
        }

        lastHandledJobId.current = jobId;
        localStorage.removeItem(INSIGHTS_PENDING_RUN_KEY);

        // Always reload insights, even if the run fails.
        // Partial results may already be saved and should still be shown.
        queryClient.invalidateQueries({ queryKey: GLOBAL_PROMPT_INSIGHTS_QUERY_KEY });

        if (status === 'succeeded') {
            showSnackbar('Insights updated', 'success');
        } else if (error === NO_CALLS_MESSAGE) {
            showSnackbar(error, 'info');
        } else {
            showSnackbar(error ?? 'Re-run failed', 'error');
        }
    }, [status, jobId, error, isConnectionError, queryClient, showSnackbar]);

    return null;
};

export default InsightsJobWatcher;
